import styled from '@emotion/styled';

import { Flex, Input, Button } from 'antd';

export const DocumentName = styled.span`
  font-weight: 400;
  color: ${({theme}) => theme.colors.black};
  line-height: 22px;
`;

export const ReferenceInput = styled(Input)`
  border: none;

  :focus {
    border-radius: 4px;
    border: none;
    border-bottom: 1px solid ${({theme}) => theme.colors.textDisabled};
    background: ${({theme}) => theme.colors.bgGrey};
    outline: none;
  }
`;

export const TableContainer = styled.div`
  && {
    .ui-table-cell {
      height: 46px;
      padding: 0 16px;
    }

    /* The selection column is narrow; the default 16px side padding clips the
       checkbox on the right. */
    .ui-table-selection-column {
      padding-inline: 8px;
    }

    /* Wrap column headers to multiple lines instead of truncating them. */
    .ui-table-thead .ui-table-cell {
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      word-break: normal;
    }

    /* Selection must not change row height: keep the same single-line layout
       as unselected rows (no forced word-break). */
    && .ui-table-row-selected {
      .ui-table-cell {
        background: ${({theme}) => theme.colors.badgeGrey};
      }
    }
  }
`;

export const DocumentNameWrapper = styled(Flex)`
  height: 100%;
  position: relative;
  cursor: pointer;
`

export const EditIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.blackSecondary};
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${DocumentNameWrapper}:hover &,
  &:focus-visible {
    opacity: 1;
  }

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const NameInput = styled(Input)`
  && {
    border: none;
    padding: 0;
    background: transparent;
    box-shadow: none;
    font-weight: 400;
    line-height: 22px;
  }
`;

export const SortHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
`;

export const SortChevron = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.blackSecondary};

  /* Fill-based chevron: fake a heavier weight for the active sort field by
     doubling a tight drop-shadow in the same color. */
  & svg {
    filter: ${({ $active }) =>
      $active
        ? 'drop-shadow(0 0 0.5px currentColor) drop-shadow(0 0 0.5px currentColor)'
        : 'none'};
  }
`;

export const ReferenceText = styled.span`
  max-width: 12ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.black};
  line-height: 22px;
`;

export const FilterLabel = styled.span`
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.blackSecondary};
`;

export const DateModeDivider = styled.hr`
  margin: 4px 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.strokeDarkGrey};
`;

export const DateModeSwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px 4px;
  font-weight: 400;
`;

export const Point = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 4px;
  background-color: ${({theme}) => theme.colors.textBlue400};
  position: absolute;
  left: -10px;
  top: 20px;
`

export const Actions = styled.div`
  width: 192px;
`;

export const PopoverItem = styled(Flex)<{ isDisabled?: boolean }>`
  height: 38px;
  align-items: center;
  gap: 8px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  padding: 8px;
  border-radius: 8px;
  font-weight: 400;
  color: ${({ theme, isDisabled }) => (isDisabled ? theme.colors.textGrey : 'inherit')};

  &:hover {
    background-color: ${({ theme, isDisabled }) =>
      isDisabled ? 'transparent' : theme.colors.bgBrightBlue200};
  }
`;

export const DownloadButton = styled(Button)`
  padding: 8px;
  background-color: ${({theme}) => theme.colors.bgGrey};
  color: ${({theme}) => theme.colors.blackSecondary};
  opacity: 1;

  :hover {
    opacity: 0.7;
  }
`

export const MoreIcon = styled.div`
  color: ${({ theme }) => theme.colors.blackSecondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const DownloadIcon = styled.div`
  color: ${({ theme }) => theme.colors.blackSecondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const BookedButton = styled(Button)`
  color: ${({theme}) => theme.colors.link};

  :disabled {
    border-color: ${({theme}) => theme.colors.textDisabled};
  }
`